import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Clear, ExpandLess, ExpandMore, FileDownload, FilterList, Refresh, Search, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { masterApi } from '../../../api/masterApi';
import { employeeApi } from '../../../api/employeeApi';
import { activityLogsApi, AttendanceLogDto, AttendanceLogsFilters } from '../../../api/activityLogsApi';
import employeePunchesApi, { EmployeePunchReportItem, MobileCheckinReportItem } from '../../../api/employeePunchesAPI';
import { punchSettingsApi, PunchSettingsPayload } from '../../../api/punchSettingsApi';
import { RootState } from '../../../store/store';
import { DropdownItem } from '../../../types';
import { FilterValues } from '../../../types/dynamicTypes';

const TABS = ['All', 'By Department', 'By Location', 'By Entity', 'Who is Login', 'Employee Mobile Checkin', 'Punch Settings'];
const ALL_ROWS_PAGE_SIZE = 100000;
const EMPTY_FILTERS: AttendanceLogsFilters = { pageNumber: 1, pageSize: 10 };

const defaultSettings = (tenantId: string): PunchSettingsPayload => ({
  tenantId,
  isSelfieRequiredCheckIn: false,
  isSelfieRequiredCheckOut: false,
  checkoutLocationMode: 'FLEXIBLE',
  isGeofenceEnabled: false,
  isWebCheckinEnabled: false,
  webCheckinDefault: false,
  isWebSelfieCheckinRequired: false,
  isWebSelfieCheckoutRequired: false,
  isEmployeeFaceAttendanceEnabled: false,
  isSupervisorEntryEnabled: false,
  isLiveTrackingEnabled: false,
});

const toPunchFilters = (filters: AttendanceLogsFilters, defaultMode?: string): FilterValues => ({
  organizationId: undefined,
  employeeId: filters.employeeId ?? undefined,
  departmentId: filters.departmentId ?? undefined,
  startDate: filters.startDate ?? undefined,
  endDate: filters.endDate ?? undefined,
  modeOfEntry: filters.modeOfEntry ?? defaultMode ?? undefined,
  punchType: filters.type ?? undefined,
  locationName: filters.locationName ?? undefined,
  entityName: filters.entityName ?? undefined,
  entityType: filters.entityType ?? undefined,
  recordedBy: filters.recordedBy ?? undefined,
  remarks: filters.remarks ?? undefined,
  viewType: undefined,
});

const ViewAllPunchesPage: React.FC = () => {
  const navigate = useNavigate();
  const tenantId = useSelector((state: RootState) => state.auth.user?.tenantId ?? '');
  const [filters, setFilters] = useState<AttendanceLogsFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AttendanceLogsFilters>(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [attendanceRows, setAttendanceRows] = useState<AttendanceLogDto[]>([]);
  const [whoIsInRows, setWhoIsInRows] = useState<EmployeePunchReportItem[]>([]);
  const [mobileRows, setMobileRows] = useState<MobileCheckinReportItem[]>([]);
  const [punchSettings, setPunchSettings] = useState<PunchSettingsPayload | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [designations, setDesignations] = useState<DropdownItem[]>([]);
  const [branches, setBranches] = useState<DropdownItem[]>([]);
  const [employees, setEmployees] = useState<{ id: string; name: string }[]>([]);
  const [dateError, setDateError] = useState('');
  
  useEffect(() => {
    masterApi.getDepartments().then(setDepartments).catch(() => {});
    masterApi.getDesignations().then(setDesignations).catch(() => {});
    masterApi.getBranches().then(setBranches).catch(() => {});
    employeeApi.getAll(1, 500).then((res) => {
      setEmployees(res.map((e) => ({ id: e.id, name: `${e.fullName} (${e.employeeCode})` })));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab <= 3) {
        const result = await activityLogsApi.getAttendanceLogs({
          ...appliedFilters,
          groupBy: activeTab === 1 ? 'Department' : activeTab === 2 ? 'Location' : activeTab === 3 ? 'Entity' : undefined,
        });
        setAttendanceRows(result.data ?? []);
        setWhoIsInRows([]);
        setMobileRows([]);
        setPunchSettings(null);
        setTotal(result.total ?? 0);
      } else if (activeTab === 4) {
        const result = await employeePunchesApi.getWhosInEmployee(toPunchFilters(appliedFilters));
        setWhoIsInRows(result);
        setAttendanceRows([]);
        setMobileRows([]);
        setPunchSettings(null);
        setTotal(result.length);
      } else if (activeTab === 5) {
        const result = await employeePunchesApi.getMobileCheckinReport(toPunchFilters(appliedFilters, 'GPS Based Entry'));
        setMobileRows(result);
        setAttendanceRows([]);
        setWhoIsInRows([]);
        setPunchSettings(null);
        setTotal(result.length);
      } else {
        const result = tenantId ? await punchSettingsApi.getByTenant(tenantId) : defaultSettings('');
        setPunchSettings(result);
        setAttendanceRows([]);
        setWhoIsInRows([]);
        setMobileRows([]);
        setTotal(0);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load punch data');
      setAttendanceRows([]);
      setWhoIsInRows([]);
      setMobileRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, appliedFilters, tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

    const handleFromDateChange = (value: string) => {
      if (filters.endDate && value > filters.endDate) {
        setDateError('From Date cannot be greater than To Date');
        return;
      }

      setDateError('');
      setFilter({ startDate: value || undefined });
    };

    const handleToDateChange = (value: string) => {
      if (filters.startDate && value < filters.startDate) {
        setDateError('To Date cannot be less than From Date');
        return;
      }

      setDateError('');
      setFilter({ endDate: value || undefined });
    };

  const setFilter = (patch: Partial<AttendanceLogsFilters>) => setFilters((prev) => ({ ...prev, ...patch }));
  const handleSearch = () => setAppliedFilters({ ...filters, pageNumber: 1 });
  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setShowAdvanced(false);
  };
  const handlePageChange = (_: unknown, page: number) => {
    const next = { pageNumber: page + 1 };
    setFilters((prev) => ({ ...prev, ...next }));
    setAppliedFilters((prev) => ({ ...prev, ...next }));
  };
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const requestedPageSize = parseInt(event.target.value, 10);
    const next = {
      pageSize: requestedPageSize === -1 ? ALL_ROWS_PAGE_SIZE : requestedPageSize,
      pageNumber: 1,
    };
    setFilters((prev) => ({ ...prev, ...next }));
    setAppliedFilters((prev) => ({ ...prev, ...next }));
  };

  const handleExport = async () => {
    if (activeTab > 3) return;
    setExporting(true);
    try {
      const { pageNumber, pageSize, groupBy, ...exportFilters } = appliedFilters;
      const blob = await activityLogsApi.exportAttendanceLogs(exportFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Attendance_Logs_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export attendance logs');
    } finally {
      setExporting(false);
    }
  };

  const renderAttendanceTable = () => (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Organization', 'Emp. Code', 'Employee Name', 'Date', 'Time', 'Type', 'Mode Of Entry', 'Location', 'View Map', 'Entity Name', 'Entity Type', 'Reason', 'Recorded By', 'Remarks'].map((c) => (
                <TableCell key={c} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'grey.50' }}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {attendanceRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.organization || '-'}</TableCell>
                <TableCell>{row.employeeCode || '-'}</TableCell>
                <TableCell>{row.employeeName || '-'}</TableCell>
                {/* <TableCell>{row.date || '-'}</TableCell> */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.date
                    ? (() => {
                        const d = new Date(row.date);

                        if (isNaN(d.getTime())) return row.date;

                        return d
                          .toLocaleDateString('en-GB')
                          .replace(/\//g, '-');
                      })()
                    : '-'}
                </TableCell>
                <TableCell>{row.time || '-'}</TableCell>
                <TableCell><Chip label={row.type} size="small" color={row.type === 'CHECK IN' ? 'success' : 'warning'} variant="outlined" /></TableCell>
                <TableCell>{row.modeOfEntry || '-'}</TableCell>
                <TableCell>{row.locationName || '-'}</TableCell>
                <TableCell>{row.viewLocation ? <Link href={row.viewLocation} target="_blank" rel="noopener noreferrer">View Map</Link> : '-'}</TableCell>
                <TableCell>{row.entityName || '-'}</TableCell>
                <TableCell>{row.entityType || '-'}</TableCell>
                <TableCell>{row.reasonProvided || '-'}</TableCell>
                <TableCell>{row.recordedBy || '-'}</TableCell>
                <TableCell>{row.remarks || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={(appliedFilters.pageNumber || 1) - 1}
        onPageChange={handlePageChange}
        rowsPerPage={appliedFilters.pageSize === ALL_ROWS_PAGE_SIZE ? -1 : (appliedFilters.pageSize || 10)}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 50, 100, { label: 'All', value: -1 }]}
      />
    </Paper>
  );

  const renderWhoIsInTable = () => (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Emp. Code', 'Employee Name', 'Department', 'Date', 'Check In', 'Mode Of Entry', 'Status'].map((c) => (
                <TableCell key={c} sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {whoIsInRows.map((row) => (
              <TableRow key={`${row.employeeId}-${row.date}`} hover>
                <TableCell>{row.employeeCode || '-'}</TableCell>
                <TableCell>{row.employeeName || '-'}</TableCell>
                <TableCell>{row.department || '-'}</TableCell>
                <TableCell>{row.date ? new Date(row.date).toLocaleDateString('en-IN') : '-'}</TableCell>
                <TableCell>{row.checkInTime ? new Date(row.checkInTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                <TableCell>{row.modeOfEntry || '-'}</TableCell>
                <TableCell><Chip label="Logged In" size="small" color="success" variant="outlined" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderMobileTable = () => (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Emp. Code', 'Employee Name', 'Date', 'Time', 'Mode Of Entry', 'Location', 'Entity', 'View Map', 'Remarks'].map((c) => (
                <TableCell key={c} sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {mobileRows.map((row) => (
              <TableRow key={`${row.employeeId}-${row.punchTime || row.checkinDate}`} hover>
                <TableCell>{row.employeeCode || '-'}</TableCell>
                <TableCell>{row.employeeName || '-'}</TableCell>
                <TableCell>{new Date(row.checkinDate).toLocaleDateString('en-IN')}</TableCell>
                <TableCell>{row.punchTime ? new Date(row.punchTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                <TableCell>{row.deviceInfo || '-'}</TableCell>
                <TableCell>{row.location || '-'}</TableCell>
                <TableCell>{row.entityName ? `${row.entityName}${row.entityType ? ` (${row.entityType})` : ''}` : '-'}</TableCell>
                <TableCell>{row.viewLocation ? <Link href={row.viewLocation} target="_blank" rel="noopener noreferrer">View Map</Link> : '-'}</TableCell>
                <TableCell>{row.remarks || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderPunchSettings = () => (
    <Stack spacing={2}>
      <Alert severity="info">This tab now shows the live punch settings for the current tenant.</Alert>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Punch Settings Summary</Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between"><Typography>Selfie on Check In</Typography><Chip size="small" label={punchSettings?.isSelfieRequiredCheckIn ? 'Enabled' : 'Disabled'} color={punchSettings?.isSelfieRequiredCheckIn ? 'success' : 'default'} /></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Selfie on Check Out</Typography><Chip size="small" label={punchSettings?.isSelfieRequiredCheckOut ? 'Enabled' : 'Disabled'} color={punchSettings?.isSelfieRequiredCheckOut ? 'success' : 'default'} /></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Geofence Monitoring</Typography><Chip size="small" label={punchSettings?.isGeofenceEnabled ? 'Enabled' : 'Disabled'} color={punchSettings?.isGeofenceEnabled ? 'success' : 'default'} /></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Web Checkin</Typography><Chip size="small" label={punchSettings?.isWebCheckinEnabled ? 'Enabled' : 'Disabled'} color={punchSettings?.isWebCheckinEnabled ? 'success' : 'default'} /></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Supervisor Entry</Typography><Chip size="small" label={punchSettings?.isSupervisorEntryEnabled ? 'Enabled' : 'Disabled'} color={punchSettings?.isSupervisorEntryEnabled ? 'success' : 'default'} /></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Checkout Location Mode</Typography><Chip size="small" label={punchSettings?.checkoutLocationMode || 'FLEXIBLE'} variant="outlined" /></Box>
          </Stack>
        </CardContent>
      </Card>
      <Box><Button variant="contained" startIcon={<Settings />} onClick={() => navigate('/attendance/punch-settings')}>Open Punch Settings</Button></Box>
    </Stack>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>View All Punches</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>View all employee attendance activity records across the organization.</Typography>

      <Card sx={{ mb: 2 }} variant="outlined">
        <CardContent sx={{ pb: '12px !important' }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="Employee" select value={filters.employeeId || ''} onChange={(e) => setFilter({ employeeId: e.target.value || undefined })}>
                <MenuItem value="">All Employees</MenuItem>
                {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
              </TextField>
            </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.startDate || ''}
                  onChange={(e) => setFilter({ startDate: e.target.value || undefined })}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0], // disable future dates
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="To Date"
                  type="date"
                  disabled={!filters.startDate}
                  InputLabelProps={{ shrink: true }}
                  value={filters.endDate || ''}
                  onChange={(e) => setFilter({ endDate: e.target.value || undefined })}
                  inputProps={{
                    min: filters.startDate || undefined, // cannot be before From Date
                    max: new Date().toISOString().split('T')[0], // cannot be future date
                  }}
                />
              </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" label="Punch Type" select value={filters.type || ''} onChange={(e) => setFilter({ type: e.target.value || undefined })}>
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="CHECK IN">Check In</MenuItem>
                <MenuItem value="CHECK OUT">Check Out</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="Mode Of Entry" select value={filters.modeOfEntry || ''} onChange={(e) => setFilter({ modeOfEntry: e.target.value || undefined })}>
                <MenuItem value="">All Modes</MenuItem>
                <MenuItem value="GPS Based Entry">GPS Based Entry</MenuItem>
                <MenuItem value="Biometric">Biometric</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Web Based Entry">Web Based Entry</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Grid container spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="Location Name" value={filters.locationName || ''} onChange={(e) => setFilter({ locationName: e.target.value || undefined })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" label="Entity Type" select value={filters.entityType || ''} onChange={(e) => setFilter({ entityType: e.target.value || undefined })}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="OFFICE">Office</MenuItem>
                <MenuItem value="SITE">Site</MenuItem>
                <MenuItem value="BRANCH">Branch</MenuItem>
              </TextField>
            </Grid>
            <Grid size="auto"><Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ textTransform: 'none' }}>
              Search
            </Button></Grid>
            <Grid size="auto"><Button variant="outlined" startIcon={<FileDownload />} onClick={handleExport} disabled={exporting || activeTab > 3} sx={{ textTransform: 'none' }}>
              {exporting ? 'Exporting...' : 'Export'}
            </Button></Grid>
            <Grid size="auto"><Button variant="text" startIcon={<Clear />} onClick={handleClear} sx={{ textTransform: 'none' }}>
              Clear
            </Button></Grid>
            <Grid size="auto"><Button variant="text" startIcon={<Refresh />} onClick={fetchData} sx={{ textTransform: 'none' }}>
              Retry
            </Button></Grid>
            <Grid size="auto"><Button variant="text" size="small" startIcon={<FilterList />} endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowAdvanced((v) => !v)} sx={{ textTransform: 'none' }}>
              More Filters
            </Button></Grid>
          </Grid>

          <Collapse in={showAdvanced}>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField fullWidth size="small" label="Department" select value={filters.departmentId || ''} onChange={(e) => setFilter({ departmentId: e.target.value || undefined })}>
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField fullWidth size="small" label="Designation" select value={filters.designationId || ''} onChange={(e) => setFilter({ designationId: e.target.value || undefined })}>
                  <MenuItem value="">All Designations</MenuItem>
                  {designations.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField fullWidth size="small" label="Branch" select value={filters.branchId || ''} onChange={(e) => setFilter({ branchId: e.target.value || undefined })}>
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField fullWidth size="small" label="Entity Name" value={filters.entityName || ''} onChange={(e) => setFilter({ entityName: e.target.value || undefined })} />
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            setActiveTab(value);
            setAppliedFilters((prev) => ({ ...prev, pageNumber: 1 }));
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none', // ✅ keeps "By Department" as-is
              fontWeight: 700,
            },
          }}
        >
          {TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box> : null}
      {!loading && activeTab <= 3 && (attendanceRows.length ? renderAttendanceTable() : <Alert severity="info">No attendance records found for the selected filters.</Alert>)}
      {!loading && activeTab === 4 && (whoIsInRows.length ? renderWhoIsInTable() : <Alert severity="info">No logged-in employees found for the selected filters.</Alert>)}
      {!loading && activeTab === 5 && (mobileRows.length ? renderMobileTable() : <Alert severity="info">No mobile check-in records found for the selected filters.</Alert>)}
      {!loading && activeTab === 6 && renderPunchSettings()}
    </Box>
  );
};

export default ViewAllPunchesPage;
